import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { CalendarCheck, ChevronDown, Mail, MessageSquare, Phone, Ticket, Users } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import Pagination from '../components/Pagination';
import { useServerPaged } from '../hooks/use-paged';
import {
  apiEnabled,
  fetchAdminSummary,
  fetchAdminBookings,
  fetchAdminRequests,
  updateBookingStatus,
  updateRequestStatus,
  resendBookingEmail,
  BOOKING_STATUSES,
  REQUEST_STATUSES,
  STATUS_LABELS,
} from '../lib/adminReservations';

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function SummaryCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f4fbfa] text-[#1fa5a3]"><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-2xl font-bold text-[#14213d]">{value ?? '—'}</p>
          <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function money(item) {
  if (item.amount == null) return null;
  return `${Number(item.amount).toFixed(2)} ${item.currency || 'EUR'}`;
}

function Row({ item, statuses, onChange, pending, isBooking }) {
  const c = item.customer || {};
  const [open, setOpen] = useState(false);
  const [mail, setMail] = useState({ sending: false, msg: '' });

  const resend = async () => {
    setMail({ sending: true, msg: '' });
    try {
      const r = await resendBookingEmail(item.id);
      setMail({ sending: false, msg: r.customer ? '✅ Email reenviado al cliente' : '✅ Enviado' });
    } catch (e) {
      setMail({ sending: false, msg: `⚠️ ${e.message || 'No se pudo enviar'}` });
    }
  };

  return (
    <div className="border-b border-slate-100">
      <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex min-w-0 items-center gap-2 text-left">
          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-[#14213d]">{item.activity_title || item.activity_id}</span>
            <span className="block truncate text-xs text-slate-500">
              {c.name || 'Sin nombre'} · {fmtDate(item.service_date)}{money(item) ? ` · ${money(item)}` : ''}
            </span>
          </span>
        </button>
        <select
          value={item.status}
          disabled={!apiEnabled || pending}
          onChange={(e) => onChange(item.id, e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#14213d] outline-none focus:border-[#1fa5a3] disabled:opacity-50"
        >
          {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
        </select>
      </div>

      {open ? (
        <div className="mb-3 rounded-2xl bg-slate-50 p-4 text-sm">
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <div><span className="text-slate-400">Cliente:</span> <span className="font-medium text-[#14213d]">{c.name || '—'}</span></div>
            <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-[#1fa5a3]" /><a href={`tel:${c.phone}`} className="font-medium text-[#14213d] hover:text-[#1fa5a3]">{c.phone || '—'}</a></div>
            <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#1fa5a3]" /><a href={`mailto:${c.email}`} className="truncate text-[#14213d] hover:text-[#1fa5a3]">{c.email || '—'}</a></div>
            <div><span className="text-slate-400">Fecha / hora:</span> <span className="font-medium text-[#14213d]">{fmtDate(item.service_date)} · {item.time_slot || '—'}</span></div>
            {isBooking ? <>
              <div><span className="text-slate-400">Importe:</span> <span className="font-semibold text-[#1fa5a3]">{money(item) || '—'}</span></div>
              <div><span className="text-slate-400">Método de pago:</span> <span className="font-medium text-[#14213d]">{item.payment_method || '—'}</span></div>
            </> : null}
            {item.notes ? <div className="sm:col-span-2"><span className="text-slate-400">Notas:</span> {item.notes}</div> : null}
          </div>
          {Array.isArray(item.line_items) && item.line_items.length ? (
            <div className="mt-3 border-t border-slate-200 pt-2">
              {item.line_items.filter((li) => li.quantity).map((li, i) => (
                <div key={i} className="flex justify-between text-xs text-slate-600">
                  <span>{li.quantity}× {li.label}</span>
                  <span>{((li.line_total_cents || 0) / 100).toFixed(2)} €</span>
                </div>
              ))}
            </div>
          ) : null}
          {isBooking ? (
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3">
              <button type="button" onClick={resend} disabled={!apiEnabled || mail.sending}
                className="inline-flex items-center gap-2 rounded-full bg-[#14213d] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0f1a31] disabled:opacity-50">
                <Mail className="h-3.5 w-3.5" />{mail.sending ? 'Enviando…' : 'Reenviar email al cliente'}
              </button>
              {mail.msg ? <span className="text-xs text-slate-600">{mail.msg}</span> : null}
              <span className="text-xs text-slate-400">Ref: {item.checkout_reference || item.booking_id || item.id}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminReservations() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('bookings');

  const bookingsPager = useServerPaged({ initialSize: 20 });
  const requestsPager = useServerPaged({ initialSize: 20 });

  const summaryQuery = useQuery({ queryKey: ['admin', 'summary'], queryFn: fetchAdminSummary, enabled: apiEnabled });
  const bookingsQuery = useQuery({
    queryKey: ['admin', 'bookings', bookingsPager.skip, bookingsPager.limit],
    queryFn: () => fetchAdminBookings(undefined, { skip: bookingsPager.skip, limit: bookingsPager.limit }),
    placeholderData: keepPreviousData,
  });
  const requestsQuery = useQuery({
    queryKey: ['admin', 'requests', requestsPager.skip, requestsPager.limit],
    queryFn: () => fetchAdminRequests(undefined, { skip: requestsPager.skip, limit: requestsPager.limit }),
    placeholderData: keepPreviousData,
  });
  const s = summaryQuery.data;

  const bookingMut = useMutation({
    mutationFn: ({ id, status }) => updateBookingStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] }),
  });
  const requestMut = useMutation({
    mutationFn: ({ id, status }) => updateRequestStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'requests'] }),
  });

  const bookings = bookingsQuery.data?.items || [];
  const requests = requestsQuery.data?.items || [];
  const bookingsTotal = bookingsQuery.data?.total || 0;
  const requestsTotal = requestsQuery.data?.total || 0;
  const bookingsPageCount = Math.max(1, Math.ceil(bookingsTotal / bookingsPager.pageSize));
  const requestsPageCount = Math.max(1, Math.ceil(requestsTotal / requestsPager.pageSize));

  return (
    <AdminSection
      eyebrow="Admin"
      title="Reservas"
      description="Gestiona reservas online y solicitudes de consulta. Cambia el estado de cada una."
      widthClassName="max-w-6xl"
    >
      {!apiEnabled ? (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          El backend no está conectado (falta <code>REACT_APP_API_BASE_URL</code>). Al desplegar la API verás aquí las reservas reales.
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard icon={Ticket} label="Reservas" value={s?.bookings_total} />
        <SummaryCard icon={CalendarCheck} label="Pagadas" value={s?.bookings_paid} />
        <SummaryCard icon={MessageSquare} label="Solicitudes pend." value={s?.requests_pending} />
        <SummaryCard icon={Users} label="Actividades" value={s?.activities} />
      </div>

      <div className="mt-8 rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="mb-4 flex gap-2">
          {[['bookings', `Reservas (${bookingsTotal})`], ['requests', `Solicitudes (${requestsTotal})`]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${tab === id ? 'bg-[#1fa5a3] text-white' : 'bg-slate-100 text-slate-600 hover:text-[#1fa5a3]'}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'bookings' ? (
          bookings.length ? (
            <>
              <div>{bookings.map((b) => <Row key={b.id} item={b} isBooking statuses={BOOKING_STATUSES} pending={bookingMut.isPending} onChange={(id, status) => bookingMut.mutate({ id, status })} />)}</div>
              <Pagination page={bookingsPager.page} pageCount={bookingsPageCount} onPageChange={bookingsPager.setPage} pageSize={bookingsPager.pageSize} onPageSizeChange={bookingsPager.setPageSize} pageSizes={[20, 50, 100]} />
            </>
          ) : <p className="py-8 text-center text-sm text-slate-500">{apiEnabled ? 'Sin reservas todavía.' : 'Sin datos (backend no conectado).'}</p>
        ) : (
          requests.length ? (
            <>
              <div>{requests.map((r) => <Row key={r.id} item={r} statuses={REQUEST_STATUSES} pending={requestMut.isPending} onChange={(id, status) => requestMut.mutate({ id, status })} />)}</div>
              <Pagination page={requestsPager.page} pageCount={requestsPageCount} onPageChange={requestsPager.setPage} pageSize={requestsPager.pageSize} onPageSizeChange={requestsPager.setPageSize} pageSizes={[20, 50, 100]} />
            </>
          ) : <p className="py-8 text-center text-sm text-slate-500">{apiEnabled ? 'Sin solicitudes todavía.' : 'Sin datos (backend no conectado).'}</p>
        )}
      </div>
    </AdminSection>
  );
}
